import { Component, Input, ChangeDetectorRef, OnChanges, ViewChild, Output, EventEmitter, SimpleChanges, ElementRef } from '@angular/core';
import { cloneDeep } from 'lodash';
import * as Color from 'color';
import { NotificationService, ErrorLoggerService } from 'app/services';
import { FormBuilderDefinition, FormBuilderField, FormBuilderFieldChangeEvent, KeyFormBuilderComponent } from 'app/shared/components/form-builder';
import { FormSectionHandler } from '../../entities/entity.handler';
import { AdminEditorService } from '../../admin.service';
import { SectionField } from '../../entities/utils';
import { DomSanitizer } from '@angular/platform-browser';
import { get, set } from 'lodash';
import { ERRORS } from 'app/shared/model/errors.model';

@Component({
    selector: 'key-admin-form-editor',
    templateUrl: './form-editor.component.html',
    styleUrls: ['./form-editor.component.scss'],
})
export class AdminFormEditorComponent implements OnChanges {

    @Input() entity: any;
    @Input() handler: FormSectionHandler<any, any>;
    @Input() editable: boolean;
    @Input() showHeader = true;

    @Output() onEditing = new EventEmitter<boolean>();
    @Output() onSaving = new EventEmitter<any>();
    @Output() onSaved = new EventEmitter<any>();
    @Output() onCanceled = new EventEmitter<any>();

    @ViewChild(KeyFormBuilderComponent) formBuilder: KeyFormBuilderComponent;

    saveable = false;
    errorMessage: string;

    errors: { [field: string]: string } = {};
    fields: FormBuilderField[];
    formDefinition: FormBuilderDefinition;
    displayValues: { name: string, value: string, unit: string, href?: string }[];

    originalValues: { [key: string]: any };
    formValues: { [key: string]: any };

    dirtyFields: FormBuilderField[] = [];
    inline = true;

    private _editing: boolean;
    get editing() { return this._editing; }
    @Input() set editing(value: boolean) {
        setTimeout(() => { // prevents an expression changed after it was checked error when used as an input
            this._editing = value;
            this.onEditing.emit(value);
            this.ref.markForCheck();
        });
    }

    constructor(
        private admin: AdminEditorService,
        private error: ErrorLoggerService,
        private notify: NotificationService,
        private ref: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
        private el: ElementRef
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if ((changes.handler && this.handler) || changes.entity) {            
            this.load();
        }
    }

    load() {
        this.errorMessage = null;
        this.errors = {};
        const bulk = !this.entity;
        return Promise.all([
            this.handler.getFields(this.entity),
            !bulk ? this.handler.mapEntityToEditor(this.entity) : {},
        ]).then(([fields, values]) => {
            this.fields = fields;
            this.originalValues = values;
            this.formValues = cloneDeep(this.originalValues);
            this.displayValues = this.getDisplayValues(this.formValues || {});
            this.formDefinition = {
                groups: [{ name: '', fields: this.fields }],
            };
            this.ref.markForCheck();
        }).catch(err => {
            this.showError(err);
        });
    }

    getDisplayValues(values: any): { name: string, value: string, unit: string }[] {
        const getColorSwatch = (color: string, name?: string) => {
            try {
                return `
                    <span class="color-picker-label small ${!color ? 'transparent' : ''}"
                        style="background-color: ${color}; border: 1px solid ${Color(color).darken(.2).string()}; vertical-align: middle; margin-right: 5px"></span> ${name || color}
                `;
            } catch {
                return `${color}`;
            }
        };

        const result = [];
        const recurse = (fields: FormBuilderField[]) => {
            fields.map((field: SectionField) => {
                const value = get(values, field.id);
                let displayValue = field.getText
                    ? field.getText(field, values)
                    : field.getValue ? field.getValue(field, values) : value;
                if (field.type === 'image' && value) {
                    displayValue = this.sanitizer.bypassSecurityTrustHtml(`<img src="${value.preview}" />`);
                }
                if (field.type === 'colorcombo') {
                    if (value !== 'inherit' && value !== 'none') {
                        const [color, name] = displayValue.split(':');
                        displayValue = this.sanitizer.bypassSecurityTrustHtml(getColorSwatch(color, name));
                    }
                }
                if (field.type === 'memo' && displayValue) {
                    const max = 10;
                    let lines = displayValue.split('\n');
                    if (lines.length > max) {
                        lines = lines.slice(0, max);
                        lines.push('...');
                    }
                    displayValue = lines.join('<br/>');
                }
                if (field.type === 'color') {
                    displayValue = this.sanitizer.bypassSecurityTrustHtml(getColorSwatch(value));
                }
                if (field.type === 'swatches') {
                    displayValue = this.sanitizer.bypassSecurityTrustHtml(field.values.map(val => getColorSwatch(values[val.key])).join(''));
                }
                if (field.type === 'password') {
                    displayValue = '********';
                }
                if (field.type === 'checkbox' || field.type === 'toggle') {
                    displayValue = value ? this.admin.i18n.instant('FORMS.CHECKBOX.YES') : this.admin.i18n.instant('FORMS.CHECKBOX.NO');
                }
                if (field.type === 'assetfilter' && Array.isArray(displayValue)) {
                    displayValue = displayValue.map(item => item.text).join(', ');
                }
                if (Array.isArray(displayValue)) {
                    displayValue = displayValue.join(', ');
                }

                result.push({
                    name: field.title,
                    value: displayValue,
                    unit: field.unit,
                    href: field.getHref ? field.getHref(field, values) : null,
                });
                if (field.values) {
                    const subfield = field.values.find(x => x.key === ((value && value.id) || value));
                    if (subfield && subfield.fields) {
                        recurse(subfield.fields);
                    }
                }
            });
        };
        recurse(this.fields);
        return result;
    }

    onStartEdit() {
        // poor man's responsive design
        const width = (this.el.nativeElement.querySelector('.center-column') as HTMLDivElement).offsetWidth;
        this.inline = (width > 250);

        this.errorMessage = null;
        this.errors = {};
        this.editing = !this.editing;
        this.ref.markForCheck();
    }

    showError(error: any) {
        if (error && error.name === ERRORS.VALIDATE_ERROR && error.data && error.data.fields) {
            this.errors = Object.keys(error.data.fields).reduce((result, field) => {
                result[field] = error.data.fields[field].message;
                return result;
            }, {});
            if (this.handler.mapValidationErrors) {
                this.errors = this.handler.mapValidationErrors(this.errors);
            }
            console.error('validationErrors', this.errors);
        } else {
            this.error.trackException(error);
            this.errorMessage = error && this.notify.translateError(error);
        }
        this.ref.markForCheck();

    }

    onFormChange(event: FormBuilderFieldChangeEvent) {
        this.errorMessage = null;
        this.errors = {};
        if (event.dirty) {
            if (!this.dirtyFields.find(x => x.id === event.field.id)) {
                this.dirtyFields.push(event.field);
            }
        }
        this.saveable = this.getChanged().fields.length > 0;
    }

    async onAction(action: string) {
        if (this.handler.actionTriggered) {
            try {
                const result = await this.handler.actionTriggered(this.entity, action);
                if (result) {
                    if (result.updatePayload) {
                        const item = await this.handler.applyUpdate(this.entity, result.updatePayload);
                        this.editing = false;
                        this.entity = item;
                        this.load();
                        this.onSaved.emit(item);
                    }
                }
            } catch (error) {
                this.showError(error);
            } finally {
                this.ref.markForCheck();
            }
        }
    }

    onCancel() {
        this.errorMessage = null;
        this.errors = {};
        this.editing = false;
        this.formValues = cloneDeep(this.originalValues); // reset values to what they were initially
        this.onCanceled.emit();
        this.ref.markForCheck();
    }

    onSave() {
        this.errorMessage = null;
        this.errors = {};
        if (this.formBuilder.validate()) {
            this.onSaving.emit();
            const changed = this.getChanged();
            this.handler.mapEditorToEntity({}, changed.values).then(updatePayload => {

                if (this.entity) {
                    return this.handler.applyUpdate(this.entity, updatePayload).then(item => {
                        this.editing = false;
                        this.entity = item;
                        this.load();
                        this.onSaved.emit(item);
                    });
                } else {
                    // this is a bulk update scenario
                    this.onCancel();
                    this.onSaved.emit(null);

                    const items = [...this.admin.selectedItems]; // clone them so nothing can change them underneath us
                    const changeList = changed.fields.map(field => ({ name: field.title, value: field.getText ? field.getText(field, changed.values) : changed.values[field.id] }));
                    return this.admin.confirmBulkUpdates(items.length, changeList).then(result => {
                        if (result) {
                            return this.admin.applyBulkUpdates(this.handler.applyUpdate.bind(this.handler), items, updatePayload);
                        }
                    });
                }
            }).catch(err => {
                this.showError(err);
                this.onSaved.emit(err);
            });

        }
        this.ref.markForCheck();
    }


    getChanged(): { fields: FormBuilderField[], values: any } {
        // only return values for dirty and actually changed fields
        const fields = [];
        const changes = {};
        this.dirtyFields.forEach(field => {
            if (field.valuesEqual && field.valuesEqual(field, get(this.originalValues, field.id), get(this.formValues, field.id))) {
                return;
            }
            if (field.type === 'swatches') { // not ideal
                field.values.forEach(val => {
                    set(changes, val.key, get(this.formValues, val.key));
                });
            } else {
                set(changes, field.id, get(this.formValues, field.id));
            }
            fields.push(field);

        });
        return { fields: fields, values: changes };
    }

}
